import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  GbpersonService,
  PersonGbMasterDetailEntryMeta,
  PersonGbMasterId,
  PersonGbVersionDetailEntryMeta,
  PersonGbVersionId
} from '../../../core/gb-api';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ActivatedRoute, Router } from '@angular/router';
import { log } from '../../../log/log';
import { TimepointService } from '../../../core/services/timepoint.service';
import { Observable, Subscription } from 'rxjs';
import { AppConfigService } from '../../../core/services/app-config.service';
import { HistoryMode } from '../../../core/services/HistoryMode';
import { mergeMap } from 'rxjs/operators';
import { AppPermissionService } from 'app/core/services/app-permission.service';
import { PermissionResponse } from 'app/core/base-api';
import { PersongbDetailTitleComponent } from './title/persongb-detail-title.component';
import { RubrikComponent } from '../../../shared/components/rubrik/rubrik.component';
import { PersongbNatuerlichePersonComponent } from './natuerliche-person/persongb-natuerliche-person.component';
import { PersonGbSchweizJuristischePersonComponent } from './schweiz-juristische-person/person-gb-schweiz-juristische-person.component';
import { PersongbAuslaendischeRechtsformComponent } from './auslaendische-rechtsform/persongb-auslaendische-rechtsform.component';
import { PersongbOeffentlRechtlKoerperschaftComponent } from './oeffentl-rechtl-koerperschaft/persongb-oeffentl-rechtl-koerperschaft.component';
import { PersongbGemeinschaftComponent } from './gemeinschaft/persongb-gemeinschaft.component';
import { PersonstammViewComponent } from '../../../stammdaten/person-stamm/view/personstamm-view.component';
import { PersongbRolleComponent } from './persongb-rolle/persongb-rolle.component';
import { CapSpinnerSmallComponent } from '../../../shared/components/cap-spinner-small/cap-spinner-small.component';
import PermissionsEnum = PermissionResponse.PermissionsEnum;
import HasAuskunftssperreEnum = PersonGbMasterDetailEntryMeta.HasAuskunftssperreEnum;

@Component({
  selector: 'cap-kompakt-persongb-detail',
  templateUrl: './persongb-detail.component.html',
  styleUrls: ['./persongb-detail.component.scss'],
  imports: [
    PersongbDetailTitleComponent,
    RubrikComponent,
    PersongbNatuerlichePersonComponent,
    PersonGbSchweizJuristischePersonComponent,
    PersongbAuslaendischeRechtsformComponent,
    PersongbOeffentlRechtlKoerperschaftComponent,
    PersongbGemeinschaftComponent,
    PersonstammViewComponent,
    PersongbRolleComponent,
    CapSpinnerSmallComponent,
    TranslatePipe
  ]
})
export class PersongbDetailComponent implements OnInit, OnDestroy {
  historyMode: HistoryMode.HistoryMode;
  personGbMasterId: PersonGbMasterId;
  personGbVersionId: PersonGbVersionId;
  egbpid: string;
  hasPermissionAuskunft: boolean = false;

  public master: PersonGbMasterDetailEntryMeta;
  public version: PersonGbVersionDetailEntryMeta;

  showTitlebutton: boolean;

  subscription: Subscription = new Subscription();

  constructor(
    private route: ActivatedRoute,
    public translate: TranslateService,
    private persongbService: GbpersonService,
    private zeitpunktService: TimepointService,
    private appConfigService: AppConfigService,
    private appPermissionService: AppPermissionService,
    private router: Router
  ) {
    this.hasPermissionAuskunft = this.appPermissionService.hasPermission(PermissionsEnum.AUSKUNFTSPERRE);
  }

  ngOnInit(): void {
    this.subscription.add(
      this.route.queryParams
        .pipe(
          mergeMap((params) => {
            this.historyMode = this.appConfigService.einstellungHistorischeDaten;
            this.personGbMasterId = <PersonGbMasterId>{ id: params['id'] };
            this.personGbVersionId = <PersonGbVersionId>{ id: params['versionId'] };
            this.egbpid = params['egbpid'];
            return this.loadPersonGb();
          })
        )
        .subscribe({
          next: (entry) => this.onSubscription(entry),
          error: (err) => {
            if (err.status === 403) {
              this.router.navigate(['/security/forbidden']);
            }
          }
        })
    );

    this.subscription.add(
      this.appConfigService.einstellungHistorischeDaten$.pipe(mergeMap(() => this.loadPersonGb())).subscribe((entry) => {
        this.onSubscription(entry);
        this.setShowTitlebutton();
      })
    );
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }

  showPersonendaten(auskunftsperre: HasAuskunftssperreEnum): boolean {
    let hasAuskunftsperre = this.hasAuskunftsperre(auskunftsperre);
    return !hasAuskunftsperre || (hasAuskunftsperre && this.hasPermissionAuskunft);
  }

  hasAuskunftsperre(auskunftsperre: HasAuskunftssperreEnum): boolean {
    return auskunftsperre === HasAuskunftssperreEnum.AUSKUNFT_SPERRE;
  }

  private loadPersonGb(): Observable<PersonGbMasterDetailEntryMeta> {
    if (this.personGbMasterId && this.personGbMasterId.id) {
      return this.persongbService.loadPersonGbById(
        this.historyMode, //
        this.personGbMasterId.id, //
        this.personGbVersionId.id, //
        this.zeitpunktService.timepointCtx.timepointIso, //
        this.zeitpunktService.timepointCtx.timepointId //
      );
    } else if (this.egbpid) {
      return this.persongbService.loadPersonGbByFachId(
        this.historyMode, //
        this.egbpid, //
        this.zeitpunktService.timepointCtx.timepointIso, //
        this.zeitpunktService.timepointCtx.timepointId //
      );
    } else {
      log.warn('No PersonGbMasterDetailEntry ID provided.');
      return Observable.create(null);
    }
  }

  private onSubscription(entry: PersonGbMasterDetailEntryMeta) {
    if (!entry) {
      return;
    }
    this.master = entry;
    if (entry.versions && entry.versions.length > 0) {
      this.version = entry.versions[0];
      if (entry.versions.length > 1) {
        log.info('Found too many versions for PersonGbVersionDetailEntry. ID: ' + this.version.id);
      }
      return;
    }
    log.warn('No PersonGbVersionDetailEntry found.');
  }

  private setShowTitlebutton() {
    if (!this.version) {
      return;
    }
    this.showTitlebutton =
      !!this.version.fremdId && this.appPermissionService.hasPermission(PermissionsEnum.ANZEIGE_VERGLEICH_GRUNDBUCH_ZPV_PERSON);
  }
}
